import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { TemplateForm } from '../components/forms';
import { TemplatePreview as WhatsAppTemplatePreview } from '../components/templates';
import { getRequestAsync, postRequestAsync } from '../../../helpers/apiHandler';
import useWhatsAppTemplate from '../../../hooks/useWhatsAppTemplate';
import { showAlert } from '../../../store/actions/alertAction';

const WhatsAppTemplateCreator = () => {
  const { waba_id, phone_number_id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { access_token } = user;

  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [, setCustomValidity] = useState(false);
  const [validityPeriod, setValidityPeriod] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  const {
    template,
    templateHeader,
    templateBody,
    templateFooter,
    templateButtons,
    bodyVariables,
    bodyVariableEntries,
    bodyTextareaRef,
    isUploading,
    uploadError,
    securityRecommendation,
    showExpiryTime,
    handleTemplateChange,
    handleBodyChange,
    handleMediaUpload,
    setTemplateHeader,
    setTemplateFooter,
    setCodeExp,
    handleAddVariable,
    handleRemoveVariable,
    handleVariableEntryChange,
    handleButtonChange,
    handleAddButton,
    handleRemoveButton,
    handleTextFormat,
    validateTemplate,
    getTemplatePayload,
    setShowExpiryTime,
    setSecurityRecommendation,
    codeExp,
  } = useWhatsAppTemplate();

  // Helper function to generate role-aware routes
  const generateRoleAwareRoute = (basePath) => {
    if (!userRole) return basePath; // Fallback to original route if role not determined yet
    
    // For manager role, use the manager dashboard routes
    if (userRole === 'manager') {
      return basePath.replace('/dashboard/whatsapp/', '/manager/dashboard/whatsapp/');
    }
    
    // For agent role, keep the original path structure
    return basePath;
  };

  // Determine user role based on Redux store user data
  const determineUserRole = () => {
    // Try different possible paths for the role from Redux store and access_token
    const userRole = user?.role || access_token?.user?.role || access_token?.role;

    if (!userRole) return;

    const role = userRole === 'manager' ? 'manager' : 'agent';
    setUserRole(role);
  };

  useEffect(() => {
    // Determine user role when component mounts
    if (user?.role || access_token?.user?.role || access_token?.role) {
      determineUserRole();
    }
    
    fetchLanguages();
  }, [access_token, user]);

  const fetchLanguages = async () => {
    setIsLoading(true);
    console.log('[TemplateCreator] Fetching template languages');

    const requestData = { headers: { Authorization: access_token.token } };
    try {
      const res = await getRequestAsync('/v2/whatsapp/wa-template-languages', requestData);
      if (res.success) {
        console.log('[TemplateCreator] Template languages received');
        setLanguages(res.data.languages);
      } else {
        console.error('[TemplateCreator] Failed to fetch template languages:', res.message);
        dispatch(showAlert('Failed to load language options', 'danger'));
      }
    } catch (error) {
      console.error('[TemplateCreator] Exception during language fetch:', error);
      dispatch(showAlert('An error occurred while loading language options', 'danger'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log('[TemplateCreator] Submit button clicked');

    // Prevent multiple submissions
    if (isSubmitting) {
      console.log('[TemplateCreator] Submission already in progress, ignoring click');
      return;
    }

    // Log current template state
    console.log('[TemplateCreator] Current template state:', {
      template,
      templateHeader,
      templateBody,
      templateFooter,
      templateButtons,
      bodyVariables,
      bodyVariableEntries,
    });

    // Perform validation with detailed error messages
    if (!template.name) {
      console.warn('[TemplateCreator] Missing template name');
      dispatch(showAlert('Please provide a template name', 'danger'));
      return;
    }

    if (!template.language) {
      console.warn('[TemplateCreator] Missing template language');
      dispatch(showAlert('Please select a language', 'danger'));
      return;
    }

    if (!templateBody.trim()) {
      console.warn('[TemplateCreator] Missing template body');
      dispatch(showAlert('Please provide a message body', 'danger'));
      return;
    }

    // Category-specific validations
    if (template.category === 'AUTHENTICATION') {
      if (bodyVariables.length === 0) {
        console.warn('[TemplateCreator] Authentication template missing variables');
        dispatch(
          showAlert('Authentication templates must include at least one variable {{1}}', 'danger')
        );
        return;
      }

      // Authentication templates don't need to check for buttons as they will be added automatically
      console.log('[TemplateCreator] Authentication template validation passed');
    } else {
      // UTILITY and MARKETING templates
      if (templateButtons.some((button) => button.type === 'OTP')) {
        console.warn('[TemplateCreator] Utility/Marketing template cannot have OTP buttons');
        dispatch(showAlert('OTP buttons can only be used with Authentication templates', 'danger'));
        return;
      }

      // Check URL buttons
      const urlButtons = templateButtons.filter((button) => button.type === 'URL');
      if (urlButtons.some((button) => !button.url || !button.url.trim())) {
        console.warn('[TemplateCreator] URL buttons missing URLs');
        dispatch(showAlert('URL buttons must include a valid URL', 'danger'));
        return;
      }

      // Check PHONE_NUMBER buttons
      const phoneButtons = templateButtons.filter((button) => button.type === 'PHONE_NUMBER');
      if (phoneButtons.some((button) => !button.phoneNumber || !button.phoneNumber.trim())) {
        console.warn('[TemplateCreator] Phone buttons missing phone numbers');
        dispatch(showAlert('Phone Number buttons must include a valid phone number', 'danger'));
        return;
      }
    }

    // Check if all buttons have text
    if (templateButtons.some((button) => !button.text || !button.text.trim())) {
      console.warn('[TemplateCreator] Buttons missing text');
      dispatch(showAlert('All buttons must have text', 'danger'));
      return;
    }

    // Check if variables have examples
    if (bodyVariables.length > 0) {
      const missingExamples =
        bodyVariables.length > bodyVariableEntries.filter((entry) => entry && entry.trim()).length;
      if (missingExamples) {
        console.warn('[TemplateCreator] Templates with variables missing examples');
        dispatch(showAlert('Please provide example values for all variables', 'danger'));
        return;
      }
    }

    // If all specific validations pass, run the general validation
    if (!validateTemplate()) {
      console.warn('[TemplateCreator] Template validation failed');
      dispatch(showAlert('Please check all fields and try again', 'danger'));
      return;
    }

    // Set submitting state to true
    setIsSubmitting(true);

    // Get the template payload from the hook
    const payload = getTemplatePayload();

    // Make sure waba_id is included in the payload
    const requestData = {
      ...payload,
      waba_id: waba_id, // Ensure waba_id is set correctly
    };

    // Make sure components is a proper JSON object, not a string
    if (typeof requestData.components === 'string') {
      try {
        requestData.components = JSON.parse(requestData.components);
      } catch (error) {
        console.error('[TemplateCreator] Error parsing components:', error);
        dispatch(showAlert('Error with template structure. Please try again.', 'danger'));
        setIsSubmitting(false);
        return;
      }
    }

    console.log('[TemplateCreator] Sending API request with data:', requestData);

    try {
      const res = await postRequestAsync(
        '/v2/whatsapp/wa-template-create',
        requestData,
        access_token.token
      );

      console.log('[TemplateCreator] API response:', res);

      if (res.success) {
        console.log('[TemplateCreator] Template created successfully');
        dispatch(showAlert('Template created successfully', 'success'));
        navigate(generateRoleAwareRoute(`/dashboard/whatsapp/phone-number-details/${waba_id}/${phone_number_id}`));
      } else {
        console.error('[TemplateCreator] Template creation failed:', res.message);
        dispatch(showAlert(res.message || 'Failed to create template', 'danger'));
        // Reset submitting state on failure
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('[TemplateCreator] Exception during template creation:', error);
      dispatch(showAlert('An error occurred while creating the template', 'danger'));
      // Reset submitting state on error
      setIsSubmitting(false);
    }
  };

  const handleLanguageChange = (opt) => {
    handleTemplateChange('language', opt[0].value);
    setSelectedLanguage(opt);
  };

  const handleCodeExpChange = (value) => {
    const newValue = Number(value);
    setCodeExp(newValue);
    setValidityPeriod(newValue);
    setCustomValidity(true);
  };

  return (
    <Container fluid className="wa-template-creator p-4">
      <Row>
        <Col md={8}>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status"></Spinner>
            </div>
          ) : (
            <TemplateForm
              isEditing={false}
              template={template}
              templateHeader={templateHeader}
              templateBody={templateBody}
              templateFooter={templateFooter}
              templateButtons={templateButtons}
              bodyVariables={bodyVariables}
              bodyVariableEntries={bodyVariableEntries}
              codeExp={validityPeriod}
              bodyTextareaRef={bodyTextareaRef}
              languages={languages}
              selectedLanguage={selectedLanguage}
              onLanguageChange={handleLanguageChange}
              onTemplateChange={handleTemplateChange}
              onBodyChange={handleBodyChange}
              setTemplateHeader={setTemplateHeader}
              setTemplateFooter={setTemplateFooter}
              setCodeExp={handleCodeExpChange}
              onAddVariable={handleAddVariable}
              onRemoveVariable={handleRemoveVariable}
              onVariableEntryChange={handleVariableEntryChange}
              onButtonChange={handleButtonChange}
              onAddButton={handleAddButton}
              onRemoveButton={handleRemoveButton}
              onTextFormat={handleTextFormat}
              handleMediaUpload={handleMediaUpload}
              isUploading={isUploading}
              uploadError={uploadError}
              validityPeriod={validityPeriod}
              setValidityPeriod={setValidityPeriod}
              securityRecommendation={securityRecommendation}
              setSecurityRecommendation={setSecurityRecommendation}
              showExpiryTime={showExpiryTime}
              setShowExpiryTime={setShowExpiryTime}
            />
          )}

          {!isLoading && (
            <div className="d-flex justify-content-end gap-2 mb-4 action-buttons">
              <div className="right-buttons">
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    navigate(
                      generateRoleAwareRoute(`/dashboard/whatsapp/phone-number-details/${waba_id}/${phone_number_id}`)
                    )
                  }
                  className="discard-btn"
                  disabled={isSubmitting}
                >
                  Discard
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Creating...
                    </>
                  ) : (
                    'Create Template'
                  )}
                </Button>
              </div>
            </div>
          )}
        </Col>

        {/* Right Preview Panel */}
        <Col md={4}>
          <div className="preview-container pb-4">
            <WhatsAppTemplatePreview
              header={templateHeader}
              headerType={template.headerType || 'TEXT'}
              headerMediaUrl={template.headerMediaPreviewUrl}
              body={templateBody}
              footer={templateFooter}
              buttons={templateButtons}
              variableEntries={bodyVariableEntries}
              isAuthentication={template.category === 'AUTHENTICATION'}
              securityRecommendation={securityRecommendation}
              codeExp={codeExp}
              showExpiryTime={showExpiryTime}
            />
            <h4 className="text-center ">Template Preview</h4>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default WhatsAppTemplateCreator;
